import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '@/lib/api';
import {
  gatheringsFromCalendar,
  type CheckinGatheringChoice,
} from '@/pages/attendance/checkin-calendar-gatherings';
import type { CheckinOccurrenceRow } from '@/pages/attendance/checkin-occurrence-utils';
import type { CalendarStreamItem } from '@/lib/calendar-stream';

interface OccurrenceRow extends CheckinOccurrenceRow {
  status: string;
}

interface AttendanceSession {
  id: string;
  occurrenceId: string;
  status: string;
}

export const useCheckinGatherings = (
  today: string,
  timezone: string,
  openSessions: AttendanceSession[],
  filterByScheduleTime: boolean,
) => {
  const calendar = useQuery({
    queryKey: ['programs', 'calendar', today, today],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: '100',
        fromDate: today,
        toDate: today,
      });
      return api.get<{ data: CalendarStreamItem[] }>(`/programs/calendar?${params}`);
    },
    staleTime: 60_000,
  });

  const occurrences = useQuery({
    queryKey: ['occurrences', 'check-in', today],
    queryFn: () => {
      const params = new URLSearchParams({
        fromDate: today,
        toDate: today,
        status: 'scheduled',
        sort: 'startsAt',
        direction: 'asc',
        pageSize: '100',
      });
      return api.get<{ data: OccurrenceRow[] }>(`/occurrences?${params}`);
    },
    staleTime: 60_000,
  });

  const openOccurrenceIds = useMemo(
    () => new Set(openSessions.map((row) => row.occurrenceId)),
    [openSessions],
  );

  const openProgramIds = useMemo(() => {
    const rows = occurrences.data?.data ?? [];
    const ids = new Set<string>();
    for (const session of openSessions) {
      const occ = rows.find((row) => row.id === session.occurrenceId);
      if (occ) ids.add(occ.programId);
    }
    return ids;
  }, [occurrences.data?.data, openSessions]);

  const baseInput = useMemo(
    () => ({
      calendarItems: calendar.data?.data ?? [],
      occurrences: occurrences.data?.data ?? [],
      date: today,
      openOccurrenceIds,
      openProgramIds,
    }),
    [
      calendar.data?.data,
      occurrences.data?.data,
      today,
      openOccurrenceIds,
      openProgramIds,
    ],
  );

  const gatheringsForDay = useMemo((): CheckinGatheringChoice[] => {
    if (!calendar.data?.data) return [];
    return gatheringsFromCalendar(baseInput);
  }, [baseInput, calendar.data?.data]);

  const gatherings = useMemo((): CheckinGatheringChoice[] => {
    if (!calendar.data?.data) return [];
    if (!filterByScheduleTime) return gatheringsForDay;
    return gatheringsFromCalendar({
      ...baseInput,
      filterByScheduleTime: true,
      timezone,
    });
  }, [baseInput, calendar.data?.data, filterByScheduleTime, gatheringsForDay, timezone]);

  return {
    gatherings,
    gatheringsForDay,
    isLoading: calendar.isLoading || occurrences.isLoading,
    invalidateKeys: [
      ['programs', 'calendar', today, today],
      ['occurrences', 'check-in', today],
    ] as const,
  };
};
